(function (root, factory) {
  'use strict';
  var exported = factory();
  if (typeof module === 'object' && module && typeof module.exports === 'object') {
    module.exports = exported;
  } else {
    root.AumaerkLieferungLogic = exported;
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ISO weekday: 1 = Mo .. 7 = So.
  // Mo/Di/Mi/Do "before cutoff" = 1 day to allow same-week next-day delivery
  // (e.g. Mon morning order → Tue delivery). All other slots unchanged.
  var PREP_MATRIX = {
    1: { before: 1, after: 2 },
    2: { before: 1, after: 2 },
    3: { before: 1, after: 2 },
    4: { before: 1, after: 2 },
    5: { before: 1, after: 4 },
    6: { before: 3, after: 3 },
    7: { before: 2, after: 2 }
  };

  var ALLOWED_DELIVERY_WEEKDAYS = [2, 3, 4, 5];

  var CUTOFF_HOUR = 14;
  var CUTOFF_MINUTE = 0;

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function toISODate(date) {
    return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate());
  }

  function parseISODate(s) {
    if (typeof s !== 'string') return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return null;
    var y = Number(m[1]);
    var mo = Number(m[2]) - 1;
    var d = Number(m[3]);
    var out = new Date(y, mo, d);
    if (out.getFullYear() !== y || out.getMonth() !== mo || out.getDate() !== d) return null;
    return out;
  }

  function isoWeekday(date) {
    var d = date.getDay();
    return d === 0 ? 7 : d;
  }

  function addDays(date, n) {
    var out = new Date(date.getTime());
    out.setDate(out.getDate() + n);
    return out;
  }

  // Anonymous Gregorian Easter algorithm. Returns Easter Sunday as local Date.
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  function austrianHolidays(year) {
    var s = new Set();
    s.add(year + '-01-01'); // Neujahr
    s.add(year + '-01-06'); // Heilige Drei Könige
    s.add(year + '-05-01'); // Staatsfeiertag
    s.add(year + '-08-15'); // Maria Himmelfahrt
    s.add(year + '-10-26'); // Nationalfeiertag
    s.add(year + '-11-01'); // Allerheiligen
    s.add(year + '-12-08'); // Maria Empfängnis
    s.add(year + '-12-25'); // Christtag
    s.add(year + '-12-26'); // Stefanitag

    var easter = easterSunday(year);
    s.add(toISODate(addDays(easter, 1)));   // Ostermontag
    s.add(toISODate(addDays(easter, 39)));  // Christi Himmelfahrt
    s.add(toISODate(addDays(easter, 50)));  // Pfingstmontag
    s.add(toISODate(addDays(easter, 60)));  // Fronleichnam

    return s;
  }

  function mergeBlackouts(holidaySet, customLines) {
    var out = new Set(holidaySet);
    if (!customLines) return out;
    for (var i = 0; i < customLines.length; i++) {
      var line = String(customLines[i] || '').trim();
      if (!line) continue;
      if (!parseISODate(line)) continue;
      out.add(line);
    }
    return out;
  }

  // Aumaerk dispatches one day before delivery. Any day on which Aumaerk
  // cannot dispatch (holidays + merchant blackouts) blocks the FOLLOWING
  // day for delivery too. This function expands a blackout set with each
  // day-after for that reason.
  //
  // Example: 04-06-2026 (Fronleichnam) is a holiday → no dispatch → no
  // delivery on 05-06-2026 either. Function returns a new set containing
  // both dates.
  function expandBlackoutsForNoDispatch(blackouts) {
    var out = new Set(blackouts);
    blackouts.forEach(function (iso) {
      var d = parseISODate(iso);
      if (!d) return;
      out.add(toISODate(addDays(d, 1)));
    });
    return out;
  }

  // Build the full blackout set for a given "now": Austrian holidays for the
  // current + next year, merged with the merchant's custom blackout lines,
  // then expanded so the day AFTER each blackout is blocked too (dispatch is
  // the day before delivery). Shared by the cart date picker and the
  // delivery-promise banner so both compute identical availability.
  function buildBlackouts(rawLines, now) {
    var nowVienna = viennaCalendarDate(now);
    var year = nowVienna.getFullYear();
    var holidays = austrianHolidays(year);
    austrianHolidays(year + 1).forEach(function (iso) { holidays.add(iso); });
    var customLines = String(rawLines || '').split(/\r?\n/);
    var merged = mergeBlackouts(holidays, customLines);
    return expandBlackoutsForNoDispatch(merged);
  }

  // Cached formatter — Intl construction is the expensive part.
  var VIENNA_FMT = null;

  function viennaFormatter() {
    if (VIENNA_FMT) return VIENNA_FMT;
    VIENNA_FMT = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Vienna',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return VIENNA_FMT;
  }

  function viennaParts(date) {
    var parts = viennaFormatter().formatToParts(date);
    var out = {};
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (p.type === 'year') out.year = Number(p.value);
      else if (p.type === 'month') out.month = Number(p.value);
      else if (p.type === 'day') out.day = Number(p.value);
      else if (p.type === 'hour') out.hour = Number(p.value);
      else if (p.type === 'minute') out.minute = Number(p.value);
    }
    if (out.hour === 24) out.hour = 0;
    return out;
  }

  function isPastCutoff(date) {
    var p = viennaParts(date);
    if (p.hour > CUTOFF_HOUR) return true;
    if (p.hour < CUTOFF_HOUR) return false;
    return p.minute >= CUTOFF_MINUTE;
  }

  function viennaCalendarDate(date) {
    var p = viennaParts(date);
    return new Date(p.year, p.month - 1, p.day);
  }

  function viennaIsoWeekday(date) {
    return isoWeekday(viennaCalendarDate(date));
  }

  function earliestDelivery(now, blackouts) {
    var orderDow = viennaIsoWeekday(now);
    var past = isPastCutoff(now);
    var prepDays = past ? PREP_MATRIX[orderDow].after : PREP_MATRIX[orderDow].before;

    var candidate = addDays(viennaCalendarDate(now), prepDays);

    for (var i = 0; i < 60; i++) {
      var dow = isoWeekday(candidate);
      var iso = toISODate(candidate);
      if (ALLOWED_DELIVERY_WEEKDAYS.indexOf(dow) !== -1 && !blackouts.has(iso)) {
        return candidate;
      }
      candidate = addDays(candidate, 1);
    }
    return candidate;
  }

  function isSelectableDate(date, blackouts, minDate) {
    if (date < minDate) return false;
    if (ALLOWED_DELIVERY_WEEKDAYS.indexOf(isoWeekday(date)) === -1) return false;
    if (blackouts.has(toISODate(date))) return false;
    return true;
  }

  var WEEKDAY_SHORT_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  var MONTH_LONG_DE = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  function formatDisplayLong(date) {
    return WEEKDAY_SHORT_DE[date.getDay()] + ', ' +
      date.getDate() + '. ' +
      MONTH_LONG_DE[date.getMonth()] + ' ' +
      date.getFullYear();
  }

  function formatDisplayShort(date) {
    return WEEKDAY_SHORT_DE[date.getDay()] + ', ' +
      date.getDate() + '. ' +
      MONTH_LONG_DE[date.getMonth()];
  }

  var WEEKDAY_LONG_DE = [
    'Sonntag', 'Montag', 'Dienstag', 'Mittwoch',
    'Donnerstag', 'Freitag', 'Samstag'
  ];

  // Full weekday + day + long month, no year (e.g. "Dienstag, 2. Juli").
  function formatDisplayFull(date) {
    return WEEKDAY_LONG_DE[date.getDay()] + ', ' +
      date.getDate() + '. ' +
      MONTH_LONG_DE[date.getMonth()];
  }

  return {
    PREP_MATRIX: PREP_MATRIX,
    ALLOWED_DELIVERY_WEEKDAYS: ALLOWED_DELIVERY_WEEKDAYS,
    CUTOFF_HOUR: CUTOFF_HOUR,
    CUTOFF_MINUTE: CUTOFF_MINUTE,
    toISODate: toISODate,
    parseISODate: parseISODate,
    isoWeekday: isoWeekday,
    addDays: addDays,
    easterSunday: easterSunday,
    austrianHolidays: austrianHolidays,
    mergeBlackouts: mergeBlackouts,
    expandBlackoutsForNoDispatch: expandBlackoutsForNoDispatch,
    viennaParts: viennaParts,
    isPastCutoff: isPastCutoff,
    viennaCalendarDate: viennaCalendarDate,
    viennaIsoWeekday: viennaIsoWeekday,
    earliestDelivery: earliestDelivery,
    isSelectableDate: isSelectableDate,
    formatDisplayLong: formatDisplayLong,
    formatDisplayShort: formatDisplayShort,
    formatDisplayFull: formatDisplayFull,
    buildBlackouts: buildBlackouts
  };
});
